import type { ApprovedCodeView, DesignationView, GradeLinkView, ResinFamily } from "../types";

export const NO_RESIN_FAMILY = "NONE";

export interface DesignationWithGrades {
  designation: DesignationView;
  gradeLinks: GradeLinkView[];
}

export interface ResinFamilyGroup {
  resinFamilyCode: string;
  displayName: string;
  designations: DesignationWithGrades[];
  gradeCount: number;
  baseCodes: ApprovedCodeView[];
}

function emptyGroup(resinFamilyCode: string, displayName: string): ResinFamilyGroup {
  return { resinFamilyCode, displayName, designations: [], gradeCount: 0, baseCodes: [] };
}

function groupFor(groups: Map<string, ResinFamilyGroup>, familyCode: string): ResinFamilyGroup {
  if (!groups.has(familyCode)) {
    groups.set(familyCode, emptyGroup(familyCode, familyCode === NO_RESIN_FAMILY ? "No resin family" : familyCode));
  }
  return groups.get(familyCode)!;
}

export function groupByResinFamily(
  designations: DesignationView[],
  gradeLinks: GradeLinkView[],
  resinFamilies: ResinFamily[],
  approvedCodes: ApprovedCodeView[] = [],
): ResinFamilyGroup[] {
  const gradeLinksByDesignationCode = new Map<string, GradeLinkView[]>();
  for (const gradeLink of gradeLinks) {
    const linksForDesignation = gradeLinksByDesignationCode.get(gradeLink.designationCode) ?? [];
    linksForDesignation.push(gradeLink);
    gradeLinksByDesignationCode.set(gradeLink.designationCode, linksForDesignation);
  }

  const groups = new Map<string, ResinFamilyGroup>();
  for (const family of resinFamilies) {
    if (!family.resinFamilyCode) continue;
    groups.set(family.resinFamilyCode, emptyGroup(family.resinFamilyCode, family.displayName ?? family.resinFamilyCode));
  }

  for (const designation of designations) {
    const group = groupFor(groups, designation.resinFamilyCode ?? NO_RESIN_FAMILY);
    const designationGradeLinks = gradeLinksByDesignationCode.get(designation.designationCode) ?? [];
    group.designations.push({ designation, gradeLinks: designationGradeLinks });
    group.gradeCount += designationGradeLinks.length;
  }

  for (const approvedCode of approvedCodes) {
    if (approvedCode.slotName !== "Base") continue;
    groupFor(groups, approvedCode.resinFamilyCode ?? NO_RESIN_FAMILY).baseCodes.push(approvedCode);
  }

  for (const group of groups.values()) {
    group.designations.sort((first, second) =>
      first.designation.designationCode.localeCompare(second.designation.designationCode),
    );
    group.baseCodes.sort((first, second) => first.code.localeCompare(second.code));
  }

  const noFamilyGroup = groups.get(NO_RESIN_FAMILY);
  groups.delete(NO_RESIN_FAMILY);
  const orderedGroups = [...groups.values()];
  if (noFamilyGroup && (noFamilyGroup.designations.length || noFamilyGroup.baseCodes.length)) {
    orderedGroups.push(noFamilyGroup);
  }
  return orderedGroups;
}

function containsText(value: string | null | undefined, searchText: string): boolean {
  return (value ?? "").toLowerCase().includes(searchText);
}

export function filterByBaseCode(
  designations: DesignationWithGrades[],
  baseCode: string | null,
): DesignationWithGrades[] {
  if (!baseCode) return designations;
  return designations.filter(({ designation }) => designation.baseCode === baseCode);
}

export function designationCountByBaseCode(group: ResinFamilyGroup): Map<string, number> {
  const counts = new Map<string, number>();
  for (const { designation } of group.designations) {
    counts.set(designation.baseCode, (counts.get(designation.baseCode) ?? 0) + 1);
  }
  return counts;
}

export function filterResinGroup(group: ResinFamilyGroup, filterText: string): DesignationWithGrades[] {
  return filterDesignations(group.designations, filterText);
}

export function filterDesignations(designations: DesignationWithGrades[], filterText: string): DesignationWithGrades[] {
  const searchText = filterText.trim().toLowerCase();
  if (!searchText) return designations;
  const matchingDesignations: DesignationWithGrades[] = [];
  for (const { designation, gradeLinks } of designations) {
    const designationMatches =
      containsText(designation.designationCode, searchText) ||
      containsText(designation.readsAs, searchText) ||
      containsText(designation.suffixText, searchText) ||
      designation.parts.some((part) => containsText(part.code, searchText));
    const matchingGradeLinks = designationMatches
      ? gradeLinks
      : gradeLinks.filter(
          (gradeLink) =>
            containsText(gradeLink.tradename, searchText) ||
            containsText(gradeLink.supplier, searchText) ||
            containsText(gradeLink.processingApplication, searchText) ||
            containsText(gradeLink.supplierCode, searchText) ||
            containsText(gradeLink.tradenameCode, searchText) ||
            containsText(gradeLink.designationAsUploaded, searchText) ||
            containsText(gradeLink.dataSource, searchText) ||
            containsText(gradeLink.hardnessText, searchText),
        );
    if (designationMatches || matchingGradeLinks.length) {
      matchingDesignations.push({ designation, gradeLinks: matchingGradeLinks });
    }
  }
  return matchingDesignations;
}
